import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';
import AppStoreBadges from '../../components/AppStoreBadges';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { getScreenshotPreview, getScreenshotPreviewSet } from '../../lib/screenshotPreview';
import { selectedCaptureSrc } from '../../lib/selectedCaptures';

const AnimatedSection: React.FC<{ children: React.ReactNode; delay?: number }> = ({ children, delay = 0 }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '0px' });
  const reducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return (
    <div
      ref={ref}
      style={
        reducedMotion
          ? undefined
          : {
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
              transition: `opacity 1.2s ease-out ${delay}ms, transform 1s ease-out ${delay}ms`
            }
      }
    >
      {children}
    </div>
  );
};

const GettingStarted: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBorder = theme === 'light' ? 'rgba(0,0,0,0.1)' : colors.border;
  const deviceBorder = theme === 'light' ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.2)';
  const shot = (base: string) =>
    `/assets/screenshots/ios-delivery/${base} - ${theme === 'dark' ? 'Dark' : 'Light'}.png`;

  const eyebrow = 'text-xs sm:text-sm font-bold uppercase tracking-[0.2em] mb-3';
  const sectionTitle = 'text-3xl sm:text-4xl font-bold mb-4';
  const sectionLead = 'text-lg sm:text-xl max-w-2xl leading-relaxed';

  const steps = [
    { number: '01', title: 'Create Your Account', description: 'Download RidePulse from the App Store and sign up with Apple or email. Quick, secure, and privacy-focused.', highlights: ['Sign in with Apple (recommended)', 'Or use your email address', 'Verify your email'] },
    { number: '02', title: 'Set Up Your Profile', description: 'Tell the community who you are. Add your riding experience, style, and a photo so others can recognize you.', highlights: ['Choose your username', 'Set riding experience level', 'Describe your riding style'] },
    { number: '03', title: 'Add Your Motorcycle', description: 'Showcase your ride in your digital garage. Add photos and details so event hosts know what bikes are coming.', highlights: ['Enter make, model, and year', 'Upload a photo', 'Add each bike you ride'] },
    { number: '04', title: 'Find Your First Event', description: 'Browse rides near you on the map or list view. Filter by type, distance, and date to find the perfect ride.', highlights: ['Browse map or list view', 'Filter by event type', 'Check distance and date'] },
    { number: '05', title: 'Join and Check In', description: 'RSVP to an event, select your motorcycle, and check in when you arrive. Chat with participants and get ready to ride.', highlights: ["Select which bike you're bringing", 'Join the event chat', 'Check in via QR code or GPS'] }
  ];

  const walkthrough = [
    { src: shot('Auth/iOS - Auth - Create Account'), cap: 'Create Your Account' },
    { src: selectedCaptureSrc('profile', theme), cap: 'Set Up Your Profile' },
    { src: selectedCaptureSrc('dashboardMap', theme), cap: 'Find Your First Event' },
    { src: shot('Event Details/iOS - Event Details'), cap: 'Join and Check In' }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* 1 · Split hero — copy adjacent to city-night editorial (no overlay; image has no text-safe area) */}
      <section className="relative">
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch">
          <div className="relative px-4 sm:px-6 py-16 md:py-28 lg:py-36 flex items-center order-2 md:order-1">
            <div className="max-w-xl w-full mx-auto md:ml-auto md:mr-0 md:pr-10 lg:pr-16 text-center md:text-left">
              <p className={eyebrow} style={{ color: dsColors.primary }}>Getting Started</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ color: heroPrimary, textWrap: 'balance' }}>
                Start Your Journey
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl mb-8 leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Join the RidePulse community in 5 simple steps. From creating your account to finding your first ride, we've made it easy to get started.
              </p>
              <div className="flex justify-center md:justify-start gap-4 flex-wrap">
                <AppStoreBadges size="large" showApple showAndroid />
              </div>
            </div>
          </div>
          <div className="order-1 md:order-2 aspect-[16/10] md:aspect-auto md:min-h-[480px] bg-cover bg-center" style={{ backgroundImage: `url('/assets/images/marketing-concepts/ridepulse-sportbike-city-night-action-v1.png')` }} role="img" aria-label="Rider on a sportbike riding through a city street at night" />
        </div>
      </section>

      {/* 2 · Five steps — numbered timeline */}
      <section className="py-16 md:py-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
          <ol className="relative">
            {steps.map((step, index) => (
              <AnimatedSection key={step.number}>
                <li className={`relative flex gap-6 sm:gap-8 ${index < steps.length - 1 ? 'pb-14' : ''}`}>
                  <div className="flex flex-col items-center flex-shrink-0">
                    <span
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-bold"
                      style={{ backgroundColor: `${dsColors.secondary}1f`, color: dsColors.secondary, border: `2px solid ${dsColors.secondary}40` }}
                    >
                      {step.number}
                    </span>
                    {index < steps.length - 1 && (
                      <span className="w-px flex-1 mt-3" style={{ backgroundColor: `${dsColors.secondary}30` }} aria-hidden="true" />
                    )}
                  </div>
                  <div className="pt-1 min-w-0">
                    <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: heroPrimary, textWrap: 'balance' }}>
                      {step.title}
                    </h2>
                    <p className="text-base sm:text-lg mb-4 leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                      {step.description}
                    </p>
                    <ul className="space-y-2">
                      {step.highlights.map((h) => (
                        <li key={h} className="flex items-start gap-2">
                          <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: dsColors.secondary }} aria-hidden="true">✓</span>
                          <span className="text-sm" style={{ color: heroSecondary }}>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </li>
              </AnimatedSection>
            ))}
          </ol>
        </div>
      </section>

      {/* 3 · The Onboarding Experience — real capture filmstrip */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <AnimatedSection>
            <div className="text-center mb-12">
              <div
                className="inline-block px-3 py-1 rounded-full mb-4 text-xs font-bold"
                style={{ backgroundColor: `${dsColors.treadGray}1f`, color: heroSecondary }}
              >
                FULL WALKTHROUGH
              </div>
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>The Onboarding Experience</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                From account creation to your first ride. Here's what the setup looks like.
              </p>
            </div>
            <ul className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 items-end">
              {walkthrough.map((w, i) => (
                <li key={w.cap} className={i % 2 === 1 ? 'md:translate-y-6' : ''}>
                  <div className="rounded-[1.6rem] border-4 overflow-hidden shadow-2xl mb-3" style={{ borderColor: deviceBorder }}>
                    <img src={getScreenshotPreview(w.src)} srcSet={getScreenshotPreviewSet(w.src)} sizes="(min-width: 1024px) 25vw, 50vw" alt={`${w.cap} screen in the RidePulse app`} className="w-full" loading="lazy" width={1320} height={2868} />
                  </div>
                  <p className="text-xs sm:text-sm font-semibold text-center" style={{ color: heroSecondary }}>
                    {w.cap}
                  </p>
                </li>
              ))}
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* 4 · Detailed guides — the operational detail lives in the help area */}
      <section className="py-16 md:py-24" style={{ backgroundColor: theme === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.02)' }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl">
          <AnimatedSection>
            <div className="text-center mb-10">
              <h2 className={sectionTitle} style={{ color: heroPrimary, textWrap: 'balance' }}>Want the details?</h2>
              <p className={`${sectionLead} mx-auto`} style={{ color: heroSecondary, textWrap: 'pretty' }}>
                Step-by-step guides for setup, privacy, safety, and more.
              </p>
            </div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {[
                { label: 'Set up your profile & preferences', to: '/help#profile-preferences' },
                { label: 'Add a motorcycle to your garage', to: '/help#add-motorcycle' },
                { label: 'Ride safely', to: '/help#ride-safely' }
              ].map((g) => (
                <li key={g.to}>
                  <Link
                    to={g.to}
                    className="block h-full p-5 rounded-xl text-center font-semibold text-sm transition-colors focus:outline-none focus-visible:ring-2"
                    style={{ backgroundColor: colors.surface, border: `1px solid ${cardBorder}`, color: heroPrimary, '--tw-ring-color': dsColors.primary } as React.CSSProperties}
                  >
                    {g.label} <span style={{ color: dsColors.primary }} aria-hidden="true">→</span>
                  </Link>
                </li>
              ))}
            </ul>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GettingStarted;
